import {
  useState,
} from 'react';
import {
  Callout,
  Field,
  Facts,
  Modal,
  TextInput,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  explainFailure,
  formatCount,
  useLoad,
} from '@affdice/portal-kit';
import {
  PlatformApi,
  type PeriodRow,
} from '../../../client.ts';
import {
  periodDeletion,
  periodStateTone,
} from '../../../screens.ts';
import { t, tCount } from '../../../messages.ts';
import { Failure, DeleteIcon, IconButton } from '../../shared.tsx';

/**
 * An operator's periods, and the one destructive thing only this console may do
 * to one.
 *
 * §19.5a and §22.6 say a locked period is never altered, and the operator's own
 * console honours that without exception — it can open a period, calculate it,
 * close it and lock it, and from there only Ch. 29 moves a figure. That is
 * right for every case those rules were written for: figures that were real
 * and are now wrong.
 *
 * It leaves one case with no answer at all. A period calculated before the
 * deals were loaded, or against a catalogue that was replaced the next day,
 * holds nothing that was ever true — so there is nothing to restate *from*,
 * and the eight-step process has no honest input. Until now the only way out
 * was deleting the brand, which is the whole relationship for one bad month.
 *
 * So the delete lives here, where deleting a brand already does, behind
 * `platform.admin` on the server and a typed confirmation on this side. The
 * operator keeps the console it had.
 *
 * Click-to-load like the panels around it: §3.9c logs per access, so nothing
 * is read until somebody asks for it.
 */
export function OperatorPeriodsPanel({ api, operatorId, shown, onShow, canWrite }: {
  api: PlatformApi;
  operatorId: string;
  shown: boolean;
  onShow: () => void;
  canWrite: boolean;
}) {
  const [version, setVersion] = useState(0);
  const [deleting, setDeleting] = useState<PeriodRow | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  /** What the last delete reported removing, kept until the next one opens. */
  const [done, setDone] = useState<string | null>(null);
  // See OperatorAffiliatesPanel — the guard has to be in the loader.
  const loaded = useLoad(
    () => (shown ? api.operatorPeriods(operatorId) : Promise.resolve(null)),
    [operatorId, shown, version],
  );

  if (!shown) {
    return (
      <Panel title={t('ops.periods.title')}>
        <Callout>
          {t('ops.periods.note')}
        </Callout>
        <div className="form-actions">
          <button className="primary" onClick={onShow}>{t('ops.periods.view')}</button>
        </div>
      </Panel>
    );
  }

  if (loaded.state === 'error') return <Panel title={t('ops.periods.title')}><Failure error={loaded.error} /></Panel>;
  if (loaded.state === 'loading' || !loaded.value) {
    return <Panel title={t('ops.periods.title')}><Loading rows={2} /></Panel>;
  }

  const view = deleting ? periodDeletion(deleting) : null;

  return (
    <Panel
      title={t('ops.periods.titleFor', { operator: loaded.value.operator })}
      aside={<span className="count count-badge">{loaded.value.items.length}</span>}
    >
      {/* Batch 2 review, finding 7 — what the last delete reported removing;
          a result, read whole. */}
      {done ? <Callout tone="note" fold={false}>{done}</Callout> : null}

      {loaded.value.items.length === 0 ? (
        <Empty>{t('ops.periods.none')}</Empty>
      ) : (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th>{t('common.period')}</th><th>{t('common.brand')}</th><th>{t('common.state')}</th>
                <th className="num">{t('ops.periods.lines')}</th><th className="num">{t('ops.periods.statements')}</th>
                <th className="num">{t('ops.periods.invoices')}</th><th className="num">{t('ops.periods.payouts')}</th>
                <th className="col-action" />
              </tr>
            </thead>
            <tbody>
              {loaded.value.items.map((p) => (
                <tr key={p.period_id}>
                  <td>{p.label}</td>
                  <td className="faint">{p.brand}</td>
                  <td><Chip tone={periodStateTone(p.state)}>{p.state}</Chip></td>
                  {/* Zeroes stay as figures here, unlike in the dialog: this is
                      a table being scanned down a column, and a blank cell in
                      the middle of one reads as missing rather than as none. */}
                  <td className="num">{formatCount(p.commission_lines)}</td>
                  <td className="num">{formatCount(p.statements)}</td>
                  <td className="num">{formatCount(p.invoices)}</td>
                  <td className="num">{formatCount(p.payout_documents)}</td>
                  <td className="num col-action">
                    {canWrite ? (
                      <IconButton
                        label={t('ops.periods.deleteRow', { brand: p.brand, label: p.label })}
                        danger
                        onClick={() => {
                          setTyped('');
                          setFailure(null);
                          setDone(null);
                          setDeleting(p);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <Callout>{loaded.value.note}</Callout>

      {deleting && view ? (
        <Modal title={t('ops.periods.deleteTitle', { phrase: view.phrase })} onClose={() => setDeleting(null)}>
          <div className="form">
            {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}

            {/* The dialog says what this period holds rather than warning in
                the abstract, because "cannot be undone" reads identically on
                an empty month and on one an affiliate has been invoiced
                from. `periodDeletion` decides which of the three this is. */}
            <Callout tone={view.severity === 'documented' ? 'error' : 'caution'}>
              {view.summary}
            </Callout>

            <Facts
              items={[
                {
                  label: t('ops.periods.removed'),
                  value: view.goes.length === 0
                    ? t('ops.periods.empty')
                    : view.goes.map((g) => g.label).join(', '),
                },
                ...(view.stays.length > 0
                  ? [{
                      label: t('ops.periods.kept'),
                      value: view.stays.map((g) => g.label).join(', '),
                    }]
                  : []),
                ...(view.unlinked > 0
                  ? [{
                      label: t('ops.periods.corrections'),
                      value: tCount('ops.periods.unlinked', view.unlinked),
                    }]
                  : []),
              ]}
            />

            <Field
              label={t('ops.periods.confirmLabel')}
              hint={view.phrase}
            >
              <TextInput value={typed} onChange={setTyped} placeholder={view.phrase} />
            </Field>

            <div className="form-actions">
              <button className="copy" onClick={() => setDeleting(null)}>{t('common.cancel')}</button>
              <button
                className="danger"
                disabled={busy || typed.trim() !== view.phrase}
                onClick={() => {
                  setBusy(true);
                  setFailure(null);
                  api
                    .deletePeriod(operatorId, deleting.period_id, { confirm_phrase: typed.trim() })
                    .then((r) => {
                      setDone(r.note);
                      setDeleting(null);
                      setVersion((v) => v + 1);
                    })
                    .catch(setFailure)
                    .finally(() => setBusy(false));
                }}
              >
                {busy ? t('ops.periods.deleting') : t('ops.periods.permanent')}
              </button>
            </div>
          </div>
        </Modal>
      ) : null}
    </Panel>
  );
}
